import React from "react";
import { Models } from "@hex-insights/app-modules";
import { ChildrenProps, NotificationContainer, NotificationRenderProps } from "@hex-insights/core";
import { PermissionRequired } from "@hex-insights/permissioning";
import {
	AcademicTerm,
	graphTypeNames,
	permissions,
	Student,
	StudentAcademicTermEnrollmentDeleteMutation,
	useUnenrollStudentFromAcademicTermMutation,
} from "../../../../Utilities";

export type StudentUnenrollButtonProps = Omit<
	Models.DeleteButtonProps<StudentAcademicTermEnrollmentDeleteMutation["deleteStudentAcademicTermEnrollment"]>,
	"deleteInstance"
> & {
	studentID: Student["id"];
	academicTermID: AcademicTerm["id"];
};

/**
 * A delete button for the StudentAcademicTermEnrollment model.
 */
export function StudentUnenrollButton({
	studentID,
	academicTermID,
	variant = "danger",
	confirmationContent = "Are you sure you want to unenroll this student from this term?",
	SuccessNotification = DefaultSuccessNotification,
	children = "Unenroll Student",
	...props
}: StudentUnenrollButtonProps) {
	const [unenroll] = useUnenrollStudentFromAcademicTermMutation();

	const performAction = React.useCallback(async () => {
		const { data, errors } = await unenroll({
			variables: {
				studentID,
				academicTermID,
			},
			update: (cache, result) => {
				if (!result.data?.unenrollStudentFromAcademicTerm) {
					return;
				}

				const { studentAcademicTermEnrollments, studentHomeRoomSectionEnrollments, studentCourseSectionEnrollments } =
					result.data.unenrollStudentFromAcademicTerm;

				for (let i = 0; i < studentAcademicTermEnrollments.ids.length; i++) {
					const id = studentAcademicTermEnrollments.ids[i];
					cache.evict({ id: cache.identify({ id, __typename: graphTypeNames.StudentAcademicTermEnrollment }) });
				}
				cache.evict({ id: "ROOT_QUERY", fieldName: "studentAcademicTermEnrollmentConnection" });

				for (let i = 0; i < studentHomeRoomSectionEnrollments.ids.length; i++) {
					const id = studentHomeRoomSectionEnrollments.ids[i];
					cache.evict({ id: cache.identify({ id, __typename: graphTypeNames.StudentHomeRoomSectionEnrollment }) });
				}
				cache.evict({ id: "ROOT_QUERY", fieldName: "studentHomeRoomSectionEnrollmentConnection" });

				for (let i = 0; i < studentCourseSectionEnrollments.ids.length; i++) {
					const id = studentCourseSectionEnrollments.ids[i];
					cache.evict({ id: cache.identify({ id, __typename: graphTypeNames.StudentCourseSectionEnrollment }) });
				}
				cache.evict({ id: "ROOT_QUERY", fieldName: "studentCourseSectionEnrollmentConnection" });

				cache.gc();
			},
		});

		const ok = !errors && !!data;
		return { data: ok, errors };
	}, [unenroll, studentID, academicTermID]);

	return (
		<Models.ActionButtonWithConfirmation
			performAction={performAction}
			variant={variant}
			confirmationContent={confirmationContent}
			SuccessNotification={SuccessNotification}
			{...props}
		>
			{children}
		</Models.ActionButtonWithConfirmation>
	);
}

function DefaultSuccessNotification(props: NotificationRenderProps) {
	return (
		<NotificationContainer variant="success" {...props}>
			Unenrolled student from academic term.
		</NotificationContainer>
	);
}

export type StudentUnenrollPermissionRequiredProps = Partial<ChildrenProps>;

const unenrollPermissions = [
	permissions.StudentAcademicTermEnrollment.Delete.Admin,
	permissions.StudentHomeRoomSectionEnrollment.Delete.Admin,
	permissions.StudentCourseSectionEnrollment.Delete.Admin,
];

export function StudentUnenrollPermissionRequired({ children }: StudentUnenrollPermissionRequiredProps) {
	return (
		<PermissionRequired requiredPermissions={unenrollPermissions} checkMethod="and">
			{children}
		</PermissionRequired>
	);
}
